import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Chip
} from '@mui/material';
import { Block, Person } from '@mui/icons-material';
import { blockUserService } from '../../services/blockUserService';
import { formatDistanceToNow } from 'date-fns';

const BlockedUsersManager = () => {
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unblockingUsers, setUnblockingUsers] = useState(new Set());
  const [error, setError] = useState(null);
  const [nextToken, setNextToken] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    loadBlockedUsers();
  }, []);

  const loadBlockedUsers = async (token = null) => {
    try {
      if (!token) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      const result = await blockUserService.getBlockedUsersWithDetails(20, token);
      
      if (result.success) {
        if (token) {
          setBlockedUsers(prev => [...prev, ...result.blockedUsers]);
        } else {
          setBlockedUsers(result.blockedUsers);
        }
        setNextToken(result.nextToken);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('Load blocked users error:', error);
      setError(error.message || 'Failed to load blocked users');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleUnblock = async (blockedUser) => {
    const userId = blockedUser.blockedID;
    setUnblockingUsers(prev => new Set([...prev, userId]));

    try {
      const result = await blockUserService.unblockUser(userId);
      
      if (result.success) {
        setBlockedUsers(prev => prev.filter(user => user.blockedID !== userId));
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('Unblock user error:', error);
      setError(error.message || 'Failed to unblock user');
    } finally {
      setUnblockingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Block />
        Blocked Users
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {blockedUsers.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Block sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Blocked Users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You haven't blocked any users yet.
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <List>
            {blockedUsers.map((blockedUser, index) => {
              const user = blockedUser.blockedUser;
              const isUnblocking = unblockingUsers.has(blockedUser.blockedID);

              return (
                <React.Fragment key={blockedUser.id}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar src={user?.avatar} sx={{ bgcolor: 'primary.main' }}>
                        {user?.displayName?.[0] || user?.username?.[0] || <Person />}
                      </Avatar>
                    </ListItemAvatar>
                    
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle1">
                            {user?.displayName || user?.username || 'Unknown User'}
                          </Typography>
                          {user?.username && user?.displayName && (
                            <Typography variant="body2" color="text.secondary">
                              @{user.username}
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary={
                        <Box mt={0.5}>
                          <Typography variant="body2" color="text.secondary">
                            Blocked {formatDistanceToNow(new Date(blockedUser.createdAt))} ago
                          </Typography>
                          {blockedUser.reason && (
                            <Chip 
                              label={blockedUser.reason} 
                              size="small" 
                              variant="outlined" 
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </Box>
                      }
                    />
                    
                    <ListItemSecondaryAction>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleUnblock(blockedUser)}
                        disabled={isUnblocking}
                        startIcon={isUnblocking ? <CircularProgress size={16} /> : null}
                      >
                        {isUnblocking ? 'Unblocking...' : 'Unblock'}
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                  
                  {index < blockedUsers.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>

          {nextToken && (
            <Box p={2} textAlign="center">
              <Button
                variant="outlined"
                onClick={() => loadBlockedUsers(nextToken)}
                disabled={loadingMore}
                startIcon={loadingMore ? <CircularProgress size={16} /> : null}
              >
                {loadingMore ? 'Loading...' : 'Load More'}
              </Button>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default BlockedUsersManager;